import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import { Flex, Spin } from 'antd';
import { AppLayout, ROUTE_ACCESS, type RoutePath } from '@/layouts/AppLayout';
import { NotFoundPage } from '@/app/NotFoundPage';
import { RequireAuth, RequireAccess } from '@/shared/auth/RequireAuth';
import { useAuth } from '@/shared/auth/AuthProvider';
import { hasAccess, type RouteAccess } from '@/shared/auth/access';
import { PATHS } from '@/routes/paths';
const CdkeyBindPage = lazy(() =>
  import('@/pages/CdkeyBindPage').then((m) => ({ default: m.CdkeyBindPage })),
);
const LoginPage = lazy(() => import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const SteamOrdersPage = lazy(() =>
  import('@/features/steamOrders/SteamOrdersPage').then((m) => ({ default: m.SteamOrdersPage })),
);
const SteamOrderRedeemPage = lazy(() =>
  import('@/features/steamOrderRedeem/SteamOrderRedeemPage').then((m) => ({
    default: m.SteamOrderRedeemPage,
  })),
);
const RedeemsPage = lazy(() =>
  import('@/features/redeems/RedeemsPage').then((m) => ({ default: m.RedeemsPage })),
);
const ProxiesPage = lazy(() =>
  import('@/features/proxies/ProxiesPage').then((m) => ({ default: m.ProxiesPage })),
);
const CdkeysPage = lazy(() =>
  import('@/features/cdkeys/CdkeysPage').then((m) => ({ default: m.CdkeysPage })),
);
const UsersPage = lazy(() =>
  import('@/features/users/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const FxQuotesPage = lazy(() =>
  import('@/features/fxQuotes/FxQuotesPage').then((m) => ({ default: m.FxQuotesPage })),
);
const RedeemFailedPage = lazy(() =>
  import('@/features/redeems/RedeemFailedPage').then((m) => ({ default: m.RedeemFailedPage })),
);
const SettingsPage = lazy(() =>
  import('@/features/profile/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
function ConsoleIndex() {
  const { me } = useAuth();
  void me;
  return <Navigate to={PATHS.steamOrders} replace />;
}
interface ConsoleRoute {
  path: RoutePath;
  element: ReactNode;
}
const CONSOLE_ROUTES: readonly ConsoleRoute[] = [
  { path: PATHS.steamOrders, element: <SteamOrdersPage /> },
  { path: PATHS.redeemWorkbench, element: <SteamOrderRedeemPage /> },
  { path: PATHS.redeems, element: <RedeemsPage /> },
  { path: PATHS.redeemFailed, element: <RedeemFailedPage /> },
  { path: PATHS.proxies, element: <ProxiesPage /> },
  { path: PATHS.cdkeys, element: <CdkeysPage /> },
  { path: PATHS.fxQuotes, element: <FxQuotesPage /> },
  { path: PATHS.users, element: <UsersPage /> },
  { path: PATHS.settings, element: <SettingsPage /> },
];
function guard(path: RoutePath, element: ReactNode): ReactNode {
  const access: RouteAccess = ROUTE_ACCESS[path];
  return <RequireAccess allow={(subject) => hasAccess(subject, access)}>{element}</RequireAccess>;
}
export function App() {
  return (
    <Suspense
      fallback={
        <Flex align="center" justify="center" style={{ minHeight: '100vh' }}>
          <Spin />
        </Flex>
      }
    >
      {useRoutes([
        { path: PATHS.bind, element: <CdkeyBindPage /> },
        { path: PATHS.login, element: <LoginPage /> },
        { path: '/bind', element: <Navigate to={PATHS.bind} replace /> },
        { path: '/login', element: <Navigate to={PATHS.login} replace /> },
        {
          path: '/console',
          element: (
            <RequireAuth loginPath={PATHS.login}>
              <AppLayout />
            </RequireAuth>
          ),
          children: [
            { index: true, element: <ConsoleIndex /> },
            ...CONSOLE_ROUTES.map((route) => ({
              path: route.path,
              element: guard(route.path, route.element),
            })),
            { path: '*', element: <NotFoundPage /> },
          ],
        },
        { path: '*', element: <NotFoundPage /> },
      ])}
    </Suspense>
  );
}
export { CONSOLE_ROUTES };
